
import { supabase } from '../integrations/supabase/client';
import { secureApiService } from './secureApiService';

class PushService {
  private registration: ServiceWorkerRegistration | null = null;
  private publicVapidKey = ''; // Will be loaded from secure config
  private subscribing = false; // Prevent multiple subscription attempts

  isConfigured(): boolean {
    return typeof this.publicVapidKey === 'string' && this.publicVapidKey.length > 50 && !this.publicVapidKey.includes('Replace');
  }

  async initialize(): Promise<boolean> {
    console.log('[PushService] Initializing push service...');

    // Load VAPID key from secure config
    try {
      const key = await (await import('./secureApiService')).secureApiService.getVapidPublicKey();
      if (key) {
        this.publicVapidKey = key;
        console.log('[PushService] VAPID key loaded from secure config');
      } else {
        console.warn('[PushService] VAPID public key not found in secure config');
      }
    } catch (e) {
      console.warn('[PushService] Failed to load VAPID key from secure config:', e);
    }

    if (!this.isConfigured()) {
      console.warn('[PushService] Public VAPID key is not configured. Skipping push setup.');
      return false;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('[PushService] Service workers not supported');
      return false;
    }

    if (!('PushManager' in window)) {
      console.warn('[PushService] Push messaging not supported');
      return false;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[PushService] Service worker registered:', this.registration);
      return true;
    } catch (error) {
      console.error('[PushService] Service worker registration failed:', error);
      return false;
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isConfigured()) {
      console.warn('[PushService] VAPID key is not configured. Skipping permission request.');
      return 'denied';
    }
    
    if (!('Notification' in window)) {
      console.warn('[PushService] Notifications not supported');
      return 'denied';
    }

    // Only request permission if not already granted or denied
    if (Notification.permission === 'default') {
      console.log('[PushService] Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('[PushService] Permission result:', permission);
      return permission;
    }

    return Notification.permission;
  }

  async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.registration) {
      console.error('[PushService] Service worker not registered');
      return null;
    }

    if (!this.isConfigured()) {
      console.warn('[PushService] VAPID key is not configured. Cannot subscribe to push.');
      return null;
    }

    if (this.subscribing) {
      console.log('[PushService] Subscription already in progress');
      return null;
    }

    this.subscribing = true;
    
    try {
      // Check if already subscribed
      const existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('[PushService] Already subscribed, saving to database');
        await this.savePushSubscription(existingSubscription);
        return existingSubscription;
      }

      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.publicVapidKey)
      });

      console.log('[PushService] Push subscription created:', subscription);
      await this.savePushSubscription(subscription);
      return subscription;
    } catch (error) {
      console.error('[PushService] Failed to subscribe to push:', error);
      return null;
    } finally {
      this.subscribing = false;
    }
  }

  private async savePushSubscription(subscription: PushSubscription): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('[PushService] No authenticated user for push subscription');
        return;
      }

      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!)))
        }
      };

      const { error } = await supabase
        .from('push_subscriptions')
        .upsert(
          {
            user_id: user.id,
            subscription: subscriptionData as any
          },
          {
            onConflict: 'user_id'
          }
        );

      if (error) {
        // Handle specific conflict errors more gracefully
        if (error.code === '23505' || error.message?.includes('duplicate key')) {
          console.log('[PushService] Subscription already exists, attempting update');
          const { error: updateError } = await supabase
            .from('push_subscriptions')
            .update({ subscription: subscriptionData as any })
            .eq('user_id', user.id);
          
          if (updateError) throw updateError;
        } else {
          throw error;
        }
      }
      console.log('[PushService] Push subscription saved to database');
    } catch (error) {
      console.error('[PushService] Failed to save push subscription:', error);
    }
  }

  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        await this.removePushSubscription();
        console.log('[PushService] Unsubscribed from push notifications');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[PushService] Failed to unsubscribe from push:', error);
      return false;
    }
  }

  private async removePushSubscription(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;
      console.log('[PushService] Push subscription removed from database');
    } catch (error) {
      console.error('[PushService] Failed to remove push subscription:', error);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async sendTestNotification(title: string, body: string): Promise<void> {
    if (Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico'
      });
    }
  }
}

export const pushService = new PushService();
export const subscribeUser = () => pushService.subscribeToPush();
